import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { PrismBanner, PrismButton } from "@prism-ui/react";
import AdminPane from "../../AdminPane";
import Loader from "../../../Loader";
import ErrorMessage from "../../../ErrorMessage";
import RatingWindowTab, { apiError, type ChapterWindow } from "./RatingWindowTab";
import VisibilityTab from "./VisibilityTab";
import WaitlistTab from "./WaitlistTab";
import OverviewTab from "./OverviewTab";
import ApplicationsTab from "./ApplicationsTab";
import ApplicationHistory from "../../../ApplicationHistory/ApplicationHistory";
import HostReviewModal from "./HostReviewModal";
import useUnsavedChangesGuard, { UNSAVED_CHANGES_MESSAGE } from "../../../../hooks/useUnsavedChangesGuard";
import useConfirm from "../../../../hooks/useConfirm";
import "./ApplicationRater.css";

const TABS = [
  "Overview",
  "Applications",
  "History",
  "Host Review",
  "Rating Window",
  "Visibility Toggle",
  "Waitlist",
] as const;
type Tab = (typeof TABS)[number];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ApplicationRater = ({ currentUser }: { currentUser?: any }) => {
  const [tab, setTab] = useState<Tab>("Overview");
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [rows, setRows] = useState<ChapterWindow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [chapterId, setChapterId] = useState<number | null>(currentUser?.chapter_id ?? null);
  const [modalOpen, setModalOpen] = useState(false);
  const [ratingDirty, setRatingDirty] = useState(false);

  const { confirm, confirmDialog } = useConfirm();
  const confirmLeave = useCallback(
    (message: string) =>
      confirm({ title: "Unsaved changes", message, confirmLabel: "Leave without saving", cancelLabel: "Stay", tone: "danger" }),
    [confirm]
  );

  useUnsavedChangesGuard(ratingDirty, confirmLeave);

  // Leaving the Rating Window tab unmounts its unsaved edits, so ask first.
  const switchTab = async (next: Tab) => {
    if (next === tab) return;
    if (ratingDirty && !(await confirmLeave(UNSAVED_CHANGES_MESSAGE))) return;
    setTab(next);
  };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get<ChapterWindow[]>("/api/rating_windows");
      setRows(data);
      setChapterId((current) => current ?? data[0]?.chapter_id ?? null);
    } catch (err) {
      setError(apiError(err, "Unable to load Application Rater settings"));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (error) return <ErrorMessage error={error} />;
  if (!rows) return <Loader />;

  return (
    <AdminPane as="div" width="wide" className="admin application-rater">
      {/* History covers every chapter, so the picker would only mislead there. */}
      {tab !== "History" && (
        <label className="application-rater-chapter">
          Chapter
          <select
            value={chapterId ?? ""}
            onChange={(e) => setChapterId(Number(e.target.value))}
            disabled={rows.length <= 1}
          >
            {rows.map((r) => (
              <option key={r.chapter_id} value={r.chapter_id}>
                {r.chapter_name}
              </option>
            ))}
          </select>
        </label>
      )}

      <div className="application-rater-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            className={tab === t ? "active" : ""}
            onClick={() => switchTab(t)}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="application-rater-panel" role="tabpanel">
        {tab === "Overview" && chapterId !== null && (
          <OverviewTab key={chapterId} chapterId={chapterId} year={year} onYearChange={setYear} />
        )}
        {tab === "Applications" && chapterId !== null && (
          <ApplicationsTab
            key={chapterId}
            chapterId={chapterId}
            chapterName={rows.find((r) => r.chapter_id === chapterId)?.chapter_name ?? ""}
            year={year}
            onOpenHostReview={() => {
              setTab("Host Review");
              setModalOpen(true);
            }}
          />
        )}
        {tab === "History" && <ApplicationHistory defaultChapterId={chapterId} />}
        {tab === "Host Review" && chapterId !== null && (
          <div>
            <PrismBanner tone="info">
              Walk the committee through each track&apos;s ranked applicants, record accept/reject decisions, and
              fill the waitlist. A demo never changes any application; a formal session updates application statuses.
            </PrismBanner>
            <div className="waitlist-heading">
              <span className="host-review-badge primary">{new Date().getFullYear()} cohort</span>
              <span className="waitlist-heading-note">Host Review always works on the current year&apos;s cohort.</span>
            </div>
            <div className="host-review-start">
              <PrismButton onPrismClick={() => setModalOpen(true)}>Start Host Review</PrismButton>
            </div>
            <HostReviewModal
              open={modalOpen}
              chapterId={chapterId}
              chapterName={rows.find((r) => r.chapter_id === chapterId)?.chapter_name}
              currentUser={currentUser}
              onClose={() => setModalOpen(false)}
            />
          </div>
        )}
        {tab === "Rating Window" && <RatingWindowTab rows={rows} onChanged={load} onDirtyChange={setRatingDirty} />}
        {tab === "Visibility Toggle" && chapterId !== null && <VisibilityTab key={chapterId} chapterId={chapterId} />}
        {tab === "Waitlist" && chapterId !== null && <WaitlistTab key={chapterId} chapterId={chapterId} />}
      </div>
      {confirmDialog}
    </AdminPane>
  );
};

export default ApplicationRater;
